import Decimal from "decimal.js";
/**
 * Enum representing the available rounding methods.
 */
export var RoundingMethod;
(function (RoundingMethod) {
    RoundingMethod[RoundingMethod["ROUND_UP"] = 0] = "ROUND_UP";
    RoundingMethod[RoundingMethod["ROUND_DOWN"] = 1] = "ROUND_DOWN";
    RoundingMethod[RoundingMethod["ROUND_HALF_UP"] = 2] = "ROUND_HALF_UP";
    RoundingMethod[RoundingMethod["ROUND_HALF_DOWN"] = 3] = "ROUND_HALF_DOWN";
    RoundingMethod[RoundingMethod["ROUND_HALF_EVEN"] = 4] = "ROUND_HALF_EVEN";
    RoundingMethod[RoundingMethod["ROUND_HALF_CEIL"] = 5] = "ROUND_HALF_CEIL";
    RoundingMethod[RoundingMethod["ROUND_HALF_FLOOR"] = 6] = "ROUND_HALF_FLOOR";
})(RoundingMethod || (RoundingMethod = {}));
/**
 * A class that represents a currency amount with precision handling using Decimal.js.
 * The class supports various rounding methods and tracks rounding errors.
 */
export class Currency {
    _value;
    roundingError;
    jsValue;
    constructor(amount, roundingError) {
        if (amount === undefined || amount === null) {
            this.value = new Decimal(0);
            this.roundingError = new Decimal(0);
        }
        else if (amount instanceof Currency) {
            this.value = amount.getValue();
            this.roundingError = amount.getRoundingError();
        }
        else if (typeof amount === "object" && "value" in amount && "roundingError" in amount) {
            return Currency.fromJSON(amount);
        }
        else {
            this.value = new Decimal(amount);
            if (roundingError !== undefined) {
                this.roundingError = new Decimal(roundingError);
            }
            else {
                this.roundingError = new Decimal(0);
            }
        }
    }
    clone() {
        return new Currency(this.value, this.roundingError);
    }
    /** Allows JS/TS to treat Currency like a plain number */
    valueOf() {
        return this.toNumber();
    }
    get value() {
        return this._value;
    }
    set value(value) {
        if (value instanceof Currency) {
            this._value = value.getValue();
        }
        else if (value instanceof Decimal) {
            this._value = value;
        }
        else {
            this._value = new Decimal(value);
        }
        this.jsValue = this._value.toNumber();
        this.roundingError = new Decimal(0);
    }
    syncJsToValues() {
        this.value = this.jsValue;
    }
    get json() {
        return {
            value: this.value.toString(),
            roundingError: this.roundingError.toString(),
        };
    }
    static RoundingMethodFromString(roundingMethodString) {
        let roundingMethod;
        switch (roundingMethodString) {
            case "ROUND_UP":
                roundingMethod = RoundingMethod.ROUND_UP;
                break;
            case "ROUND_DOWN":
                roundingMethod = RoundingMethod.ROUND_DOWN;
                break;
            case "ROUND_HALF_UP":
                roundingMethod = RoundingMethod.ROUND_HALF_UP;
                break;
            case "ROUND_HALF_DOWN":
                roundingMethod = RoundingMethod.ROUND_HALF_DOWN;
                break;
            case "ROUND_HALF_EVEN":
                roundingMethod = RoundingMethod.ROUND_HALF_EVEN;
                break;
            case "ROUND_HALF_CEIL":
                roundingMethod = RoundingMethod.ROUND_HALF_CEIL;
                break;
            case "ROUND_HALF_FLOOR":
                roundingMethod = RoundingMethod.ROUND_HALF_FLOOR;
                break;
            default:
                roundingMethod = RoundingMethod.ROUND_HALF_EVEN;
        }
        return roundingMethod;
    }
    static fromJSON(data) {
        if (!data) {
            console.trace("Currency.fromJSON: No data provided");
            return Currency.Zero();
        }
        if (typeof data === "string") {
            data = JSON.parse(data);
        }
        if (!data.value) {
            console.warn("Currency.fromJSON: No value found in data", data);
            return Currency.Zero();
        }
        const value = new Decimal(data.value);
        const roundingError = new Decimal(data.roundingError || 0);
        return new Currency(value, roundingError);
    }
    static Zero() {
        return new Currency(0);
    }
    static get zero() {
        return Currency.Zero();
    }
    static of(amount) {
        return new Currency(amount);
    }
    /**
     * Returns the smaller of two Currency instances.
     * @param a - First Currency instance.
     * @param b - Second Currency instance.
     * @returns The Currency instance with the smaller value.
     */
    static min(a, b) {
        return a.getValue().lessThanOrEqualTo(b.getValue()) ? a : b;
    }
    isPositive() {
        return this.value.isPositive();
    }
    negated() {
        return Currency.of(this.value.negated());
    }
    isNegative() {
        return this.value.isNegative();
    }
    toNumber() {
        return this.value.toNumber();
    }
    abs() {
        return Currency.of(this.value.abs());
    }
    getValue() {
        return this.value;
    }
    get decimal() {
        return this.value;
    }
    isZero() {
        return this.value.isZero();
    }
    getRoundingError() {
        return this.roundingError;
    }
    getRoundingErrorAsCurrency() {
        return Currency.of(this.roundingError);
    }
    round(decimalPlaces = 2, method = RoundingMethod.ROUND_HALF_UP) {
        const originalValue = this.value;
        let roundedValue;
        switch (method) {
            case RoundingMethod.ROUND_UP:
                roundedValue = this.value.toDecimalPlaces(decimalPlaces, Decimal.ROUND_UP);
                break;
            case RoundingMethod.ROUND_DOWN:
                roundedValue = this.value.toDecimalPlaces(decimalPlaces, Decimal.ROUND_DOWN);
                break;
            case RoundingMethod.ROUND_HALF_UP:
                roundedValue = this.value.toDecimalPlaces(decimalPlaces, Decimal.ROUND_HALF_UP);
                break;
            case RoundingMethod.ROUND_HALF_DOWN:
                roundedValue = this.value.toDecimalPlaces(decimalPlaces, Decimal.ROUND_HALF_DOWN);
                break;
            case RoundingMethod.ROUND_HALF_EVEN:
                roundedValue = this.value.toDecimalPlaces(decimalPlaces, Decimal.ROUND_HALF_EVEN);
                break;
            case RoundingMethod.ROUND_HALF_CEIL:
                roundedValue = this.value.toDecimalPlaces(decimalPlaces, Decimal.ROUND_CEIL);
                break;
            case RoundingMethod.ROUND_HALF_FLOOR:
                roundedValue = this.value.toDecimalPlaces(decimalPlaces, Decimal.ROUND_FLOOR);
                break;
            default:
                roundedValue = this.value.toDecimalPlaces(decimalPlaces, Decimal.ROUND_HALF_UP);
        }
        const roundingError = originalValue.minus(roundedValue);
        const roundedCurrency = Currency.of(roundedValue);
        roundedCurrency.roundingError = roundingError;
        return roundedCurrency;
    }
    /**
     * Returns the value rounded to the specified number of decimal places.
     * Defaults to two decimal places.
     * @param decimalPlaces - The number of decimal places to round to. Defaults to 2.
     * @param method - The rounding method to use. Defaults to ROUND_HALF_UP.
     * @returns {Decimal} - The rounded Decimal value.
     */
    getRoundedValue(decimalPlaces = 2, method = RoundingMethod.ROUND_HALF_UP) {
        return this.value.toDecimalPlaces(decimalPlaces, this.getRoundingMode(method));
    }
    getRoundedNumber(decimalPlaces = 2, method = RoundingMethod.ROUND_HALF_UP) {
        return this.value.toDecimalPlaces(decimalPlaces, this.getRoundingMode(method)).toNumber();
    }
    getRoundingMode(method) {
        switch (method) {
            case RoundingMethod.ROUND_UP:
                return Decimal.ROUND_UP;
            case RoundingMethod.ROUND_DOWN:
                return Decimal.ROUND_DOWN;
            case RoundingMethod.ROUND_HALF_UP:
                return Decimal.ROUND_HALF_UP;
            case RoundingMethod.ROUND_HALF_DOWN:
                return Decimal.ROUND_HALF_DOWN;
            case RoundingMethod.ROUND_HALF_EVEN:
                return Decimal.ROUND_HALF_EVEN;
            case RoundingMethod.ROUND_HALF_CEIL:
                return Decimal.ROUND_CEIL;
            case RoundingMethod.ROUND_HALF_FLOOR:
                return Decimal.ROUND_FLOOR;
            default:
                return Decimal.ROUND_HALF_UP;
        }
    }
    add(amount) {
        if (amount instanceof Currency) {
            return Currency.of(this.value.plus(amount.value));
        }
        else {
            return Currency.of(this.value.plus(amount));
        }
    }
    subtract(amount) {
        if (amount instanceof Currency) {
            return Currency.of(this.value.minus(amount.value));
        }
        else {
            return Currency.of(this.value.minus(amount));
        }
    }
    multiply(amount) {
        if (amount instanceof Currency) {
            return Currency.of(this.value.times(amount.value));
        }
        else {
            return Currency.of(this.value.times(amount));
        }
    }
    divide(amount) {
        // Check for division by zero
        let divisor;
        if (amount instanceof Currency) {
            divisor = amount.value;
        }
        else {
            divisor = new Decimal(amount);
        }
        if (divisor.isZero()) {
            throw new Error("Division by zero is not allowed");
        }
        if (amount instanceof Currency) {
            return Currency.of(this.value.dividedBy(amount.value));
        }
        else {
            return Currency.of(this.value.dividedBy(amount));
        }
    }
    toCurrencyString() {
        return this.value.toFixed(2);
    }
    toJSON() {
        return this.toCurrencyString();
    }
    /**
     * @deprecated Use toJSON instead.
     */
    toJson() {
        return this.toJSON();
    }
    /**
     * Checks if the value is equal to another Currency's value.
     * @param other - The other Currency instance to compare with.
     * @returns True if values are equal, false otherwise.
     */
    equals(other) {
        return this.value.equals(other.getValue());
    }
    greaterThan(other) {
        if (other instanceof Currency) {
            return this.value.greaterThan(other.getValue());
        }
        else {
            return this.value.greaterThan(other);
        }
    }
    lessThan(other) {
        if (other instanceof Currency) {
            return this.value.lessThan(other.getValue());
        }
        else {
            return this.value.lessThan(other);
        }
    }
    greaterThanOrEqualTo(other) {
        if (other instanceof Currency) {
            return this.value.greaterThanOrEqualTo(other.getValue());
        }
        else {
            return this.value.greaterThanOrEqualTo(other);
        }
    }
    lessThanOrEqualTo(other) {
        if (other instanceof Currency) {
            return this.value.lessThanOrEqualTo(other.getValue());
        }
        else {
            return this.value.lessThanOrEqualTo(other);
        }
    }
}
